import React, { useEffect, useState } from 'react'
import Menu from 'components/menu'
import DeleteModal from 'components/delete'
import { Archive, Delete, ExternalLink, FolderInput, Pencil, Plus } from 'components/icons'
import { useHubFolders } from 'hooks/useHubFolders'
import { IContextMenuState, IHubFolder, ISidebarMoveItem, ISidebarResource, SidebarResourceKind } from 'types'
import SidebarMenuList, { MENU_SEPARATOR, SidebarMenuEntry, SidebarMenuItem } from 'components/sidebar/menuItems'
import { CreateActions, createMenuItems } from 'components/sidebar/components/resourcemenu'
import {
  SidebarNode,
  countResources,
  findNode,
  subfolderFits,
  subtreeIds
} from 'components/sidebar/components/folders/tree'
import { dragGroup } from 'components/sidebar/components/folders/dnd'
import MoveToModal from 'components/sidebar/components/folders/MoveToModal'
import ArchiveExplanation from 'components/delete/ArchiveExplanation'

/** The row a menu was opened on, and where the menu goes. */
export interface RowMenuTarget {
  id: string
  kind: SidebarNode['kind']
  name: string
  /** The page a document or table row opens; null for a folder, or a table with no view to open. */
  href: string | null
  /** What opened the menu (the row, or its ⋯ button), which gets focus back when the menu closes. */
  row: HTMLElement
  top: number
  left: number
}

/**
 * Where a row's context menu opens: at the pointer, or, for one opened from the keyboard (Shift+F10
 * or the Menu key, which report no pointer position), just below the row.
 */
export const contextMenuPosition = (event: React.MouseEvent<HTMLElement>): { top: number; left: number } => {
  if (event.clientX !== 0 || event.clientY !== 0) return { top: event.clientY, left: event.clientX }
  const rect = event.currentTarget.getBoundingClientRect()
  return { top: rect.bottom, left: rect.left }
}

/**
 * Once the next frame has rendered, focuses the first of `rowIds` that is on screen; null stands
 * for the tree itself. For after a move or delete, which re-render or remove the row.
 */
export const focusFirstRow = (rowIds: Array<string | null>): void => {
  requestAnimationFrame(() => {
    for (const id of rowIds) {
      const selector = id === null ? '[data-sidebar-tree]' : `[data-row-id="${CSS.escape(id)}"]`
      const element = document.querySelector<HTMLElement>(selector)
      if (element) {
        element.focus()
        return
      }
    }
  })
}

const ONLY_OWNERS_MANAGE_FOLDERS = {
  rename: 'Only hub Owners can rename folders.',
  move: 'Only hub Owners can move folders.',
  delete: 'Only hub Owners can delete folders.'
}
const ONLY_OWNERS_MOVE_RESOURCE = "Only hub Owners and this resource's Owners can move it."

/** Why this viewer can't move a row of this kind. */
const moveRefusal = (kind: SidebarNode['kind']): string =>
  kind === 'folder' ? ONLY_OWNERS_MANAGE_FOLDERS.move : ONLY_OWNERS_MOVE_RESOURCE

const KIND_NAME: Record<SidebarNode['kind'], string> = { folder: 'subfolder', document: 'document', table: 'table' }

/**
 * Why this viewer can't rename this document or table: they don't own it, or the row is part of a
 * selection of several, as Rename acts on one at a time. Undefined when they can.
 */
const renameRefusal = (kind: SidebarResourceKind, owns: boolean, inSelection: boolean): string | undefined => {
  if (!owns) return `Only hub Owners and this ${KIND_NAME[kind]}'s Owners can rename it.`
  if (inSelection) return `Select just this ${KIND_NAME[kind]} to rename it.`
  return undefined
}

/** Why this viewer can't archive this document or table: they don't own it. Undefined when they can. */
const archiveRefusal = (kind: SidebarResourceKind, owns: boolean): string | undefined =>
  owns ? undefined : `Only hub Owners and this ${KIND_NAME[kind]}'s Owners can archive it.`

/** "Delete & archive 8 items", or just "Delete" when there's nothing inside to archive. */
const deleteLabel = (archives: number): string =>
  archives === 0 ? 'Delete' : `Delete & archive ${archives} ${archives === 1 ? 'item' : 'items'}`

/** The documents and tables anywhere inside `node`, top to bottom. */
const resourcesUnder = (node: SidebarNode | undefined): ISidebarResource[] =>
  node?.kind === 'folder'
    ? node.children.flatMap((child) => (child.kind === 'folder' ? resourcesUnder(child) : [child.resource]))
    : []

/** The folders inside `node`, deepest first, so each is empty of subfolders by the time it goes. */
const subfoldersDeepestFirst = (node: SidebarNode | undefined): string[] =>
  node?.kind === 'folder'
    ? node.children.flatMap((child) => (child.kind === 'folder' ? [...subfoldersDeepestFirst(child), child.id] : []))
    : []

/** The documents and tables among `ids` that are still in the tree, in that order. */
const resourcesIn = (tree: SidebarNode[], ids: string[]): ISidebarResource[] =>
  ids.flatMap((id) => {
    const node = findNode(tree, id)
    return node && node.kind !== 'folder' ? [node.resource] : []
  })

/**
 * The archive confirmation's body for several documents and tables: which ones, and what archiving
 * does. After an attempt, `refused` holds why each one left couldn't be archived (null: no reason given).
 */
const ArchiveSelectionExplanation: React.FC<{
  resources: ISidebarResource[]
  refused: Map<string, string | null>
}> = ({ resources, refused }) => {
  const documents = resources.filter((r) => r.kind === 'document').length
  const tables = resources.length - documents
  return (
    <div data-testid="archive-selection-explanation">
      {refused.size > 0 && <div className="mb-2.5">These couldn&apos;t be archived:</div>}
      <ul className="list-disc pl-5 max-h-40 overflow-y-auto">
        {resources.map((r) => (
          <li key={`${r.kind}-${r.publicId}`}>
            {r.name}
            {refused.has(r.publicId) && (
              <span className="text-red"> — {refused.get(r.publicId) ?? "couldn't be archived"}</span>
            )}
          </li>
        ))}
      </ul>
      {documents > 0 && <ArchiveExplanation kind="document" plural={documents > 1} />}
      {tables > 0 && <ArchiveExplanation kind="table" plural={tables > 1} />}
    </div>
  )
}

interface FolderMenuProps {
  target: RowMenuTarget
  tree: SidebarNode[]
  folders: IHubFolder[]
  selected: string[]
  visible: string[]
  /** Hub Owners: folders' own actions. Anyone else sees them disabled, with why. */
  canManage: boolean
  /** Owners and Builders: the Create submenu, for documents and tables in a folder. */
  canCreate: boolean
  /** The viewer may move this row (Move to…). */
  canMove: boolean
  /** The viewer owns this document or table (hub Owners own them all): Rename and Archive. */
  ownsRow: boolean
  /** The row is part of a selection of several: Rename doesn't act on it, and Archive archives them all. */
  inSelection: boolean
  createActions: CreateActions
  /** The menu, and any modal it opened, has gone. */
  onClose: () => void
  /** Opens the row's rename box. */
  onRename: (id: string) => void
  /** A Move to… that carried the selection went through. */
  onSelectionMoved: () => void
  /** Shows in the tree what an archive or delete would take away (these rows), or stops (null). */
  onPreview: (ids: string[] | null) => void
}

type Phase =
  | { kind: 'menu' }
  | { kind: 'archive' }
  | { kind: 'archiveSelection'; ids: string[]; refused: Map<string, string | null> }
  /** Deletes these folders, archiving all they hold, and archives these documents and tables beside them. */
  | { kind: 'deleteFolders'; folderIds: string[]; resourceIds: string[]; refused: Map<string, string | null> }
  | { kind: 'move'; items: ISidebarMoveItem[] }

/** Every document and table a folder delete archives: those inside `folderIds`, and `resourceIds` beside them. */
const deletionContents = (tree: SidebarNode[], folderIds: string[], resourceIds: string[]): ISidebarResource[] => [
  ...resourcesIn(tree, resourceIds),
  ...folderIds.flatMap((id) => resourcesUnder(findNode(tree, id)))
]

/** The rows a confirmation is about to take away, which stay red in the tree while it's open. */
const previewFor = (phase: Phase, targetId: string, tree: SidebarNode[]): string[] | null => {
  if (phase.kind === 'archive') return [targetId]
  if (phase.kind === 'archiveSelection') return phase.ids
  if (phase.kind === 'deleteFolders') {
    const folders = phase.folderIds.flatMap((id) => {
      const folder = findNode(tree, id)
      return folder ? subtreeIds(folder) : []
    })
    return [...folders, ...phase.resourceIds]
  }
  return null
}

interface DeleteFoldersModalProps {
  tree: SidebarNode[]
  folderIds: string[]
  resourceIds: string[]
  refused: Map<string, string | null>
  onCancel: () => void
  onDeleted: () => void
  deleteFolders: () => Promise<void>
}

/**
 * The confirmation for deleting folders: it lists every document and table that gets archived with
 * them. A folder with nothing inside to archive is just deleted, with its empty subfolders.
 */
const DeleteFoldersModal: React.FC<DeleteFoldersModalProps> = ({
  tree,
  folderIds,
  resourceIds,
  refused,
  onCancel,
  onDeleted,
  deleteFolders
}) => {
  const contents = deletionContents(tree, folderIds, resourceIds)
  const folderNames = folderIds.map((id) => findNode(tree, id)?.name ?? '')
  const what = folderIds.length === 1 ? `'${folderNames[0]}'` : `${folderIds.length} folders`
  const subfolders = folderIds.flatMap((id) => subfoldersDeepestFirst(findNode(tree, id))).length
  const emptyNote =
    subfolders === 0
      ? `${folderIds.length === 1 ? 'The folder is' : 'The folders are'} empty.`
      : `Only empty subfolders are inside, and they're deleted too.`
  return (
    <DeleteModal
      id="sidebar-delete-folder-modal"
      open
      setOpen={(open) => {
        if (!open) onCancel()
      }}
      resourceName={what}
      customHeading={
        contents.length === 0 ? `Delete ${what}?` : `Delete ${what} and archive ${countResources(contents)}?`
      }
      confirmLabel={contents.length === 0 ? 'Delete' : 'Delete & archive'}
      submittingLabel={contents.length === 0 ? 'Deleting…' : 'Archiving…'}
      confirmByTyping={false}
      closeOnSuccess={false}
      onSuccess={onDeleted}
      otherContent={
        contents.length === 0 ? (
          <div data-testid="delete-folder-explanation">{emptyNote}</div>
        ) : (
          <ArchiveSelectionExplanation resources={contents} refused={refused} />
        )
      }
      deleteResource={deleteFolders}
    />
  )
}

/**
 * A row's menu, from a right-click or its ⋯ button. A folder offers New subfolder and a Create ›
 * submenu (the same create options as its +), then Rename, Move to… and Delete; a document or table
 * offers Open in new tab, then Rename and Move to…, then Archive. What the viewer can't do is shown
 * disabled, with why. Move to… on a row that is part of the selection moves the whole selection (the
 * drag's rules, dragGroup); on any other row it moves just that row, as in Finder. The folder actions
 * always act on the folder the menu was opened on. A document's or table's Rename acts on one row
 * only, so it's disabled on a row that is part of a selection of several; Archive on such a row
 * archives every document and table in the selection (and is disabled if it holds a folder).
 */
const FolderMenu: React.FC<FolderMenuProps> = ({
  target,
  tree,
  folders,
  selected,
  visible,
  canManage,
  canCreate,
  canMove,
  ownsRow,
  inSelection,
  createActions,
  onClose,
  onRename,
  onSelectionMoved,
  onPreview
}) => {
  const { deleteFolder, archiveResource, archiveResources } = useHubFolders()
  const [phase, setPhase] = useState<Phase>({ kind: 'menu' })
  const node = findNode(tree, target.id)
  // Where the row was when the menu opened: focus goes there if the row is taken away.
  const [parentId] = useState(() => node?.parentId ?? null)
  // A refresh can rename the row, or take it away, while the menu is open; or take away everything
  // a Move to… or an archive of the selection was carrying (someone else deleted it, or a refused move
  // found it gone).
  const name = node?.name ?? target.name
  const archiving = phase.kind === 'archiveSelection' ? resourcesIn(tree, phase.ids) : []
  // An archive takes its rows away as it succeeds, which closes the menu here too.
  const rowGone =
    ((phase.kind === 'menu' || phase.kind === 'archive') && !node) ||
    (phase.kind === 'move' && phase.items.every((item) => !findNode(tree, item.id))) ||
    (phase.kind === 'archiveSelection' && archiving.length === 0)
  const inDialog = phase.kind !== 'menu'
  // What an archive or delete takes away shows red in the tree while it's pointed at, and while it's confirmed.
  const [pointingAt, setPointingAt] = useState<string[] | null>(null)
  const preview = previewFor(phase, target.id, tree) ?? (phase.kind === 'menu' ? pointingAt : null)
  const previewKey = preview?.join(' ') ?? ''

  useEffect(() => {
    onPreview(preview)
  }, [previewKey])
  useEffect(() => () => onPreview(null), [])
  const previewOnHover =
    (ids: string[]) =>
    (on: boolean): void =>
      setPointingAt(on ? ids : null)

  useEffect(() => {
    if (!rowGone) return
    // Focus was in the menu, which goes with the row.
    focusFirstRow([parentId, null])
    onClose()
  }, [rowGone])

  // Escape closes a dialog (Modal ignores defaultPrevented), and must not also clear the selection.
  // Capture runs before the selection's own document listener.
  useEffect(() => {
    if (!inDialog) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') event.preventDefault()
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [inDialog])

  /** Nothing happened to the row: what opened the menu gets focus back, without jumping the sidebar. */
  const close = (): void => {
    if (target.row.isConnected) target.row.focus({ preventScroll: true })
    else focusFirstRow([target.id])
    onClose()
  }

  if (rowGone) return null

  const menuState: IContextMenuState = {
    open: phase.kind === 'menu',
    top: `${target.top}px`,
    left: `${target.left}px`,
    right: 'auto',
    bottom: 'auto'
  }

  const openInNewTab = (href: string, kind: SidebarResourceKind): SidebarMenuItem => ({
    testId: 'folder-menu-open-new-tab',
    icon: <ExternalLink />,
    label: 'Open in new tab',
    // One tab per click: browsers block the rest as pop-ups.
    disabledReason: inSelection ? `Select just this ${KIND_NAME[kind]} to open it in a new tab.` : undefined,
    onSelect: () => {
      window.open(href, '_blank', 'noopener,noreferrer')
      close()
    }
  })
  const moveItem: SidebarMenuItem = {
    testId: 'folder-menu-move-to',
    icon: <FolderInput />,
    label: 'Move to…',
    disabledReason: canMove ? undefined : moveRefusal(target.kind),
    onSelect: () => {
      const items = dragGroup(tree, folders, target.id, selected, visible)
      if (items.length === 0) close()
      else setPhase({ kind: 'move', items })
    }
  }

  const folderItems = (): SidebarMenuEntry[] => {
    const { folder: newSubfolder, resources } = createMenuItems({
      folderId: target.id,
      canManage,
      canCreate,
      folderFits: subfolderFits(tree, target.id),
      testIdPrefix: 'folder-menu',
      actions: createActions,
      close
    })
    const createItem: SidebarMenuItem | null =
      resources.length > 0
        ? { testId: 'folder-menu-create', icon: <Plus />, label: 'Create', submenu: resources }
        : null
    const renameItem: SidebarMenuItem = {
      testId: 'folder-menu-rename',
      icon: <Pencil />,
      label: 'Rename',
      disabledReason: canManage ? undefined : ONLY_OWNERS_MANAGE_FOLDERS.rename,
      onSelect: () => {
        close()
        onRename(target.id)
      }
    }
    const archives = resourcesUnder(node).length
    const deleteItem: SidebarMenuItem = {
      testId: 'folder-menu-delete',
      icon: archives === 0 ? <Delete /> : <Archive />,
      label: deleteLabel(archives),
      variant: 'destructive',
      disabledReason: canManage ? undefined : ONLY_OWNERS_MANAGE_FOLDERS.delete,
      onHighlight: canManage && node ? previewOnHover(subtreeIds(node)) : undefined,
      onSelect: () => setPhase({ kind: 'deleteFolders', folderIds: [target.id], resourceIds: [], refused: new Map() })
    }
    const createGroup = [newSubfolder, createItem].filter((item): item is SidebarMenuItem => item !== null)
    return [...createGroup, MENU_SEPARATOR, renameItem, moveItem, MENU_SEPARATOR, deleteItem]
  }
  const resourceItems = (kind: SidebarResourceKind): SidebarMenuEntry[] => {
    const renameItem: SidebarMenuItem = {
      testId: 'folder-menu-rename',
      icon: <Pencil />,
      label: 'Rename',
      disabledReason: renameRefusal(kind, ownsRow, false),
      onSelect: () => {
        close()
        onRename(target.id)
      }
    }
    const archiveItem: SidebarMenuItem = {
      testId: 'folder-menu-archive',
      icon: <Archive />,
      label: 'Archive',
      variant: 'destructive',
      disabledReason: archiveRefusal(kind, ownsRow),
      onHighlight: ownsRow ? previewOnHover([target.id]) : undefined,
      onSelect: () => setPhase({ kind: 'archive' })
    }
    const open = target.href ? [openInNewTab(target.href, kind)] : []
    return [...open, MENU_SEPARATOR, renameItem, moveItem, MENU_SEPARATOR, archiveItem]
  }
  /**
   * On a row that is part of a selection of several, the menu acts on the selection: Move to… and
   * Archive (or, when it holds folders, Delete & archive) take all of it; one-row actions are disabled.
   */
  const selectionItems = (): SidebarMenuEntry[] => {
    const one = target.kind === 'folder' ? 'folder' : KIND_NAME[target.kind]
    const renameItem: SidebarMenuItem = {
      testId: 'folder-menu-rename',
      icon: <Pencil />,
      label: 'Rename',
      disabledReason: `Select just this ${one} to rename it.`,
      onSelect: () => undefined
    }
    const nodes = selected.flatMap((id) => findNode(tree, id) ?? [])
    const folderIds = nodes.filter((n) => n.kind === 'folder').map((n) => n.id)
    const resourceIds = nodes.filter((n) => n.kind !== 'folder').map((n) => n.id)
    const destructiveItem: SidebarMenuItem =
      folderIds.length === 0
        ? {
            testId: 'folder-menu-archive',
            icon: <Archive />,
            label: 'Archive',
            variant: 'destructive',
            onHighlight: previewOnHover(selected),
            onSelect: () => setPhase({ kind: 'archiveSelection', ids: selected, refused: new Map() })
          }
        : {
            testId: 'folder-menu-delete',
            icon: <Archive />,
            label: deleteLabel(
              resourceIds.length + folderIds.flatMap((id) => resourcesUnder(findNode(tree, id))).length
            ),
            variant: 'destructive',
            disabledReason: canManage ? undefined : ONLY_OWNERS_MANAGE_FOLDERS.delete,
            onHighlight: canManage ? previewOnHover(nodes.flatMap(subtreeIds)) : undefined,
            onSelect: () => setPhase({ kind: 'deleteFolders', folderIds, resourceIds, refused: new Map() })
          }
    const open = target.href && target.kind !== 'folder' ? [openInNewTab(target.href, target.kind)] : []
    return [...open, MENU_SEPARATOR, renameItem, moveItem, MENU_SEPARATOR, destructiveItem]
  }
  const items = (): SidebarMenuEntry[] => {
    if (inSelection) return selectionItems()
    return target.kind === 'folder' ? folderItems() : resourceItems(target.kind)
  }

  /** After a move, focus follows the row, or its destination if that's closed, or else the tree. */
  const moved = (movedItems: ISidebarMoveItem[], destination: string | null): void => {
    if (selected.includes(target.id)) onSelectionMoved()
    const lead = movedItems.find((item) => item.id === target.id) ?? movedItems[0]
    focusFirstRow([lead.id, destination, null])
    onClose()
  }

  /**
   * Archives what's left of the selection. Any refused stay in the dialog, each with why; the
   * rejection keeps the dialog open for another try. All archived, the rows go and the menu closes.
   */
  const archiveSelection = (ids: string[]): Promise<void> =>
    archiveResources(resourcesIn(tree, ids)).then((failures) => {
      if (failures.length === 0) return
      setPhase({
        kind: 'archiveSelection',
        ids,
        refused: new Map(failures.map((f) => [f.resource.publicId, f.reason]))
      })
      throw new Error(`${countResources(failures.map((f) => f.resource))} couldn't be archived.`)
    })

  /**
   * Archives every document and table inside the folders, and those beside them, then deletes the
   * folders, each one's subfolders first. Any refused keep the folders: they stay in the dialog, each
   * with why, for another try.
   */
  const deleteFolders = async (folderIds: string[], resourceIds: string[]): Promise<void> => {
    const failures = await archiveResources(deletionContents(tree, folderIds, resourceIds))
    if (failures.length > 0) {
      setPhase({
        kind: 'deleteFolders',
        folderIds,
        resourceIds,
        refused: new Map(failures.map((f) => [f.resource.publicId, f.reason]))
      })
      throw new Error(`${countResources(failures.map((f) => f.resource))} couldn't be archived.`)
    }
    for (const folderId of folderIds) {
      const folder = findNode(tree, folderId)
      for (const subfolderId of subfoldersDeepestFirst(folder)) await deleteFolder(subfolderId)
      await deleteFolder(folderId)
    }
  }

  /** After a delete, focus goes to the folder it was in, or the tree for the top level. */
  const deleted = (): void => {
    focusFirstRow([parentId, null])
    onClose()
  }

  return (
    <>
      <Menu id="sidebar-row-menu" menuState={menuState} setMenuState={close} width={200} fixed>
        <SidebarMenuList
          label={inSelection ? `Actions for ${selected.length} selected` : `Actions for ${name}`}
          heading={inSelection ? `${selected.length} selected` : undefined}
          items={items()}
          onClose={close}
        />
      </Menu>
      {phase.kind === 'move' && (
        <MoveToModal
          items={phase.items}
          itemName={findNode(tree, phase.items[0].id)?.name ?? name}
          tree={tree}
          onClose={close}
          onMoved={(destination) => moved(phase.items, destination)}
        />
      )}
      {phase.kind === 'archive' && node && node.kind !== 'folder' && (
        // The same confirmation as the document's or table's own settings menu.
        <DeleteModal
          id="sidebar-archive-resource-modal"
          open
          setOpen={(open) => {
            if (!open) close()
          }}
          resourceName={name}
          closeOnSuccess={false}
          otherContent={<ArchiveExplanation kind={node.kind} />}
          deleteResource={() => archiveResource(node.resource)}
        />
      )}
      {phase.kind === 'archiveSelection' && (
        <DeleteModal
          id="sidebar-archive-selection-modal"
          open
          setOpen={(open) => {
            if (!open) close()
          }}
          resourceName={countResources(archiving)}
          customHeading={`Archive ${countResources(archiving)}?`}
          confirmLabel="Archive"
          submittingLabel="Archiving…"
          confirmByTyping={false}
          closeOnSuccess={false}
          otherContent={<ArchiveSelectionExplanation resources={archiving} refused={phase.refused} />}
          deleteResource={() => archiveSelection(phase.ids)}
        />
      )}
      {phase.kind === 'deleteFolders' && (
        <DeleteFoldersModal
          tree={tree}
          folderIds={phase.folderIds}
          resourceIds={phase.resourceIds}
          refused={phase.refused}
          onCancel={close}
          onDeleted={deleted}
          deleteFolders={() => deleteFolders(phase.folderIds, phase.resourceIds)}
        />
      )}
    </>
  )
}

export default FolderMenu
